/**
 * @fileoverview Context to inject page-specific filter controls into the global FlyingMenu.
 * @module shared/contexts/FilterMenuSlotContext
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'

/**
 * Slot payload for FlyingMenu.
 */
export interface FilterMenuSlotState {
  /** React content to render inside the Filters panel below the top row */
  content: ReactNode | null
  /** Hide the Aggregation selector in the top row when true */
  hideAggregation?: boolean
  /** Hide the Profit Basis toggle in the top row when true */
  hideProfitBasis?: boolean
  /** Override the max-width of the FlyingMenu container (e.g. 'max-w-screen-2xl') */
  maxWidthClass?: string
}

interface FilterMenuSlotContextValue {
  slot: FilterMenuSlotState
  setSlot: (next: FilterMenuSlotState) => void
  clearSlot: () => void
}

const FilterMenuSlotContext = createContext<FilterMenuSlotContextValue | undefined>(undefined)

interface FilterMenuSlotProviderProps {
  children: ReactNode
  defaultMaxWidthClass?: string
}

/**
 * Provider for the FlyingMenu slot.
 *
 * @component
 * @example
 * ```tsx
 * <FilterMenuSlotProvider>
 *   <AppChrome />
 * </FilterMenuSlotProvider>
 * ```
 */
export function FilterMenuSlotProvider({
  children,
  defaultMaxWidthClass = 'max-w-screen-xl',
}: FilterMenuSlotProviderProps): ReactElement {
  const createDefaultState = useCallback<() => FilterMenuSlotState>(
    () => ({
      content: null,
      hideAggregation: false,
      hideProfitBasis: false,
      maxWidthClass: defaultMaxWidthClass,
    }),
    [defaultMaxWidthClass]
  )

  const [slot, setInternal] = useState<FilterMenuSlotState>(createDefaultState)

  useEffect(() => {
    setInternal(createDefaultState())
  }, [createDefaultState])

  // Stable callbacks to avoid triggering effects that depend on them
  const setSlotCb = useCallback((next: FilterMenuSlotState) => setInternal(next), [])
  const clearSlotCb = useCallback(() => setInternal(createDefaultState()), [createDefaultState])

  const value: FilterMenuSlotContextValue = useMemo(
    () => ({ slot, setSlot: setSlotCb, clearSlot: clearSlotCb }),
    [slot, setSlotCb, clearSlotCb]
  )

  return <FilterMenuSlotContext.Provider value={value}>{children}</FilterMenuSlotContext.Provider>
}

/**
 * Accessor hook for the FlyingMenu slot.
 *
 * @returns Slot state and mutators
 * @throws if used outside FilterMenuSlotProvider
 */
// eslint-disable-next-line react-refresh/only-export-components -- Justification: colocate provider and its hook for discoverability and DX.
export function useFilterMenuSlot(): FilterMenuSlotContextValue {
  const ctx = useContext(FilterMenuSlotContext)
  if (!ctx) throw new Error('useFilterMenuSlot must be used within a FilterMenuSlotProvider')
  return ctx
}
